import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Phase } from "@shared/types";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { Page, RichLoader, ErrorBox } from "../components/ui";
import { useDelayedFlag } from "../lib/useDelayedFlag";
import { useWaitMessage } from "../lib/useWaitMessage";
import { WorldCard } from "../components/WorldCard";
import { useT } from "../i18n";
import type { MessageKey } from "../i18n/en";

const CHIPS: { key: Phase | "all"; label: MessageKey }[] = [
  { key: "all", label: "world.filter.all" },
  { key: "live", label: "world.filter.live" },
  { key: "soon", label: "world.filter.upcoming" },
  { key: "done", label: "world.filter.decided" },
];

// Cross-country "Around the World" feed with phase filter chips.
export default function World() {
  const t = useT();
  const nav = useNavigate();
  const [filter, setFilter] = useState<Phase | "all">("all");
  // Fetch the whole feed once; chips filter client-side so switching is instant.
  const { data, loading, error } = useAsync(() => api.world(), []);
  const showRich = useDelayedFlag(loading, 400);
  const waitKey = useWaitMessage(loading);
  const shown = (data ?? []).filter((e) => filter === "all" || e.phase === filter);

  return (
    <Page>
      <h1 className="text-xl font-semibold my-3">🌍 {t("world.title")}</h1>
      <p className="text-muted text-sm mb-1">{t("world.blurb")}</p>
      {/* Only in-app entry point to the leaderboard — keep it discoverable. */}
      <Link to="/leaderboard" className="text-accent text-sm inline-block mb-4">🗳️ {t("leaderboard.title")} →</Link>

      <div className="flex gap-2 mb-5">
        {CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setFilter(c.key)}
            className={`text-sm px-3 py-1 rounded-full border transition ${
              filter === c.key ? "border-accent text-accent bg-accent/10" : "border-border text-muted hover:text-accent"
            }`}
          >
            {t(c.label)}
          </button>
        ))}
      </div>

      {showRich && <RichLoader headline={t("common.loadingElections")} message={t(waitKey)} />}
      {error && <ErrorBox msg={error} />}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-3">
        {shown.map((e) => (
          <WorldCard key={e.id} e={e} onOpen={() => nav(`/c/${e.countryId}/e/${e.id}`)} />
        ))}
      </div>
      {!loading && !error && shown.length === 0 && (
        <p className="text-muted text-sm">
          {/* The feed only carries elections that exist as records; schedules for
              races that haven't opened live on each country's page. Say so
              instead of a bare "nothing here" on the Upcoming chip. */}
          {filter === "soon" ? t("world.emptyUpcoming") : t("world.empty")}
        </p>
      )}
    </Page>
  );
}
